import type {
  DownloadFile,
  ExternalSuggestions,
  FileInfo,
  GetTokens,
} from './types';
import type {Schemas} from './dto.generated';
import {exceptionToPromiseReject} from './exceptionToPromiseReject';
import {httpDelete, httpGet, httpPost} from './http/ebba';

export const moveManifestationToExistingWork = (
  manifestationId: string,
  targetWorkId: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<void> => {
  return exceptionToPromiseReject(() =>
    httpPost<Schemas.MoveManifestationToExistingWorkDto>({
      subdir: 'manifestation/{manifestationId}/moveToExistingWork',
      subdirParams: {manifestationId},
      body: {workId: targetWorkId},
      mock: !!mock,
      getTokens,
    }).then(() => {}),
  );
};

export const moveManifestationToDuplicategWork = (
  manifestationId: string,
  fromWorkId: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<string> => {
  return exceptionToPromiseReject(() =>
    httpPost<{workId: string}>({
      subdir: 'manifestation/{manifestationId}/moveToDuplicateWork',
      subdirParams: {manifestationId},
      body: {workId: fromWorkId},
      mock: !!mock,
      getTokens,
    }).then(({workId}) => workId),
  );
};

export const getManifestationExternalSuggestions = (
  manifestationId: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<ExternalSuggestions> => {
  return exceptionToPromiseReject(() =>
    httpGet<Schemas.ExternalSuggestionsDto>({
      subdir: 'manifestation/{manifestationId}/externalSuggestions',
      subdirParams: {manifestationId},
      mock: !!mock,
      getTokens,
    }),
  );
};

export const deleteManifestation = (
  manifestationId: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<void> => {
  return exceptionToPromiseReject(() =>
    httpDelete({
      subdir: 'manifestation/v2/{manifestationId}',
      subdirParams: {manifestationId},
      mock: !!mock,
      getTokens,
    }),
  );
};

/**
 * Triggered when changerequest is revoked, because backend (Meta) needs us to trigger a outbox update anyway.
 * https://bokbasen.jira.com/browse/BB-6488
 */
export const postManifestationOutboxTrigger = (
  manifestationId: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<void> => {
  return exceptionToPromiseReject(() => {
    return httpPost<void>({
      subdir: 'manifestation/v2/{manifestationId}/outbox',
      subdirParams: {manifestationId},
      mock: !!mock,
      getTokens,
    });
  });
};

export const postManifestationPhysicalContentReceived = (
  ean: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<Schemas.PhysicalContentReceivedResponse> => {
  return exceptionToPromiseReject(() => {
    return httpPost<Schemas.PhysicalContentReceivedResponse>({
      subdir: 'manifestation/{ean}/physicalcontentreceived',
      subdirParams: {ean},
      mock: !!mock,
      getTokens,
    });
  });
};

export const getManifestationFileInfo = (
  ean: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<FileInfo> => {
  return exceptionToPromiseReject(() =>
    httpGet<Schemas.FileInfoDto>({
      subdir: 'manifestation/{ean}/fileinfo',
      subdirParams: {ean},
      mock: !!mock,
      getTokens,
    }),
  );
};

export const getManifestationDownloadFileInfo = (
  ean: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<DownloadFile> => {
  return exceptionToPromiseReject(() =>
    httpGet<Schemas.DownloadFileDto>({
      subdir: 'manifestation/{ean}/download',
      subdirParams: {ean},
      mock: !!mock,
      getTokens,
    }),
  );
};
